import { CircleAlert, CircleCheck, CircleDot, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn, type StatusTone } from '@giyus/ui';

export interface AttentionItem {
  readonly id: string;
  /** Título; passe um link (`<Link>`) para levar o usuário à tela em que ele age. */
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Prazo/origem já formatados (ex.: "Venceu em 28/10/2026 · Tarefa"). */
  readonly meta?: ReactNode;
  readonly tone?: StatusTone;
  /** Texto do tom para leitores de tela (ex.: "Atrasado"); o tom nunca depende só da cor. */
  readonly toneLabel?: string;
  /** Ação à direita (ex.: botão/link "Abrir"). */
  readonly action?: ReactNode;
}

export interface AttentionListProps {
  /** Já ordenados por urgência pelo chamador. */
  readonly items: readonly AttentionItem[];
  /** Nome acessível da lista (ex.: "Itens que precisam da sua atenção"). */
  readonly label: string;
  readonly className?: string;
}

const TONE: Record<StatusTone, { className: string; Icon: typeof Info }> = {
  success: {
    className: 'border-success-border bg-success-subtle text-success-subtle-foreground',
    Icon: CircleCheck,
  },
  warning: {
    className: 'border-warning-border bg-warning-subtle text-warning-subtle-foreground',
    Icon: TriangleAlert,
  },
  danger: {
    className: 'border-danger-border bg-danger-subtle text-danger-subtle-foreground',
    Icon: CircleAlert,
  },
  info: {
    className: 'border-info-border bg-info-subtle text-info-subtle-foreground',
    Icon: Info,
  },
  neutral: { className: 'border-border-control bg-surface text-muted-foreground', Icon: CircleDot },
};

/** Fila "Precisa da sua atenção": lista semântica de itens com ícone + texto por urgência e ação opcional. */
export function AttentionList({ items, label, className }: AttentionListProps) {
  return (
    <ul
      data-slot="attention-list"
      aria-label={label}
      className={cn('flex flex-col divide-y divide-border', className)}
    >
      {items.map((item) => {
        const tone = item.tone ?? 'neutral';
        const { className: toneClass, Icon } = TONE[tone];
        return (
          <li
            key={item.id}
            data-tone={tone}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3 first:pt-0 last:pb-0"
          >
            <span
              aria-hidden="true"
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-pill border',
                toneClass,
              )}
            >
              <Icon className="size-4" />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-body font-medium text-heading">
                {item.toneLabel ? <span className="sr-only">{item.toneLabel}: </span> : null}
                {item.title}
              </span>
              {item.description ? (
                <span className="text-body-sm text-foreground-secondary">{item.description}</span>
              ) : null}
              {item.meta ? (
                <span className="text-caption text-muted-foreground">{item.meta}</span>
              ) : null}
            </div>
            {item.action ? <div className="shrink-0 text-body-sm">{item.action}</div> : null}
          </li>
        );
      })}
    </ul>
  );
}
